import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  Form,
  TextInput,
  regex,
  required,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { AdminDataProvider } from '../dataProvider';

/**
 * Store-tag create (Phase 8, D-multi-channel-editor). A tag is key / label /
 * description. The KEY is the stable ref rate rules scope by, so it is set once
 * here and immutable thereafter (the edit shows it read-only). New tags get an
 * `action='create'` audit entry through the #25 wrapper; there is no audit code
 * here. Gated `stores:write` (editor and up); a viewer is denied at the route.
 */

const keyValidators = [
  required('Key is required'),
  regex(
    /^[a-z][a-z0-9_]*$/,
    'Key must be lowercase letters, digits and underscores',
  ),
];

interface StoreTagFormValues {
  key: string;
  label: string;
  description: string;
}

/** The create view. Exported for testing without the access wrapper. */
export const StoreTagCreateView = () => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as StoreTagFormValues;
    try {
      // Guard the stable key's uniqueness (it is the rate-rule scope ref).
      const existing = await dataProvider.getList('storeTags', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: { key: values.key },
      });
      if (existing.total && existing.total > 0) {
        notify('That key is already in use', { type: 'error' });
        return;
      }
      const { data } = await dataProvider.create('storeTags', {
        data: {
          key: values.key,
          label: values.label,
          description: values.description,
        },
      });
      notify('Tag created', { type: 'success' });
      redirect('edit', 'storeTags', data.id);
    } catch {
      notify('Could not create the tag', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        New store tag
      </Typography>
      <Form onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextInput
              source="key"
              label="Key"
              validate={keyValidators}
              helperText="The stable reference, e.g. top_stores. Cannot be changed later."
              fullWidth
            />
            <TextInput
              source="label"
              label="Label"
              validate={required('Label is required')}
              fullWidth
            />
            <TextInput
              source="description"
              label="Description"
              validate={required('Description is required')}
              multiline
              fullWidth
            />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Create
          </Button>
          <Button type="button" onClick={() => redirect('list', 'storeTags')}>
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** Gated entry point registered as the `storeTags` Resource's create. */
export const StoreTagCreate = () => (
  <RequireAccess capability="stores:write">
    <StoreTagCreateView />
  </RequireAccess>
);
